import React from 'react';
import {
  Box, Typography, Paper, List, ListItem, ListItemIcon, ListItemText,
  Avatar, Divider, Stack, Link
} from '@mui/material';
import { Link as RouterLink } from "react-router-dom";
import CircleIcon from '@mui/icons-material/Circle';
import { nombreDe, fotoDe } from '../../common/imparte';

export const BulletItem = ({ text, color = 'secondary.light' }) => (
  <ListItem sx={{ alignItems: 'flex-start', py: 0.5, px: 0 }}>
    <ListItemIcon sx={{ minWidth: 28, mt: 0.8 }}>
      <CircleIcon sx={{ fontSize: 16, color }} />
    </ListItemIcon>
    <ListItemText
      primary={text}
      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', lineHeight: 1.6 }}
    />
  </ListItem>
);

// `imparten`: quién imparte el servicio, con su rol y su presentación (lo manda el servidor). Por omisión es quien lo creó; si su
// dueño lo puso en un grupo o negocio con otra persona, es esa persona y `grupo` dice de qué página. `user`/`profile` son el
// proveedor (quien lo creó): con él se reserva y se cotiza.
const ServiceContent = ({ service, profile, user, imparten, grupo }) => {
  const { descripcion, incluye, infoAdicional } = service;
  const proveedor = user ? { ...user, rol: profile?.perfil?.rol, desc: profile?.perfil?.desc } : null;
  const personas = Array.isArray(imparten) && imparten.length > 0 ? imparten : (proveedor ? [proveedor] : []);
  const nombreProveedor = nombreDe(user) || profile?.nombre || '';
  // Split includes and additional info by lines if they come as strings
  const includesList = typeof incluye === 'string' ? incluye.split('\n').filter(i => i.trim() !== '') : incluye || [];
  const additionalInfoList = typeof infoAdicional === 'string' ? infoAdicional.split('\n').filter(i => i.trim() !== '') : infoAdicional || [];

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight="bold" color="primary.main">
          Sobre el servicio
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Conoce más detalles
        </Typography>
      </Box>

      <Paper elevation={0} sx={{ p: { xs: 2.5, md: 4 }, borderRadius: '12px', border: 1, borderColor: 'sutil', backgroundColor: 'white' }}>
        {/* Description Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
            Descripción
          </Typography>
          {/* Se respeta lo que escribió el proveedor (saltos de línea); si no escribió nada se dice, sin inventar texto */}
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, whiteSpace: 'pre-line', wordBreak: 'break-word', fontStyle: descripcion ? 'normal' : 'italic' }}>
            {descripcion || 'Este servicio aún no tiene descripción.'}
          </Typography>
        </Box>

        {/* Qué incluye Section */}
        {includesList.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Divider sx={{ mb: 4 }} />
            <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
              Qué incluye
            </Typography>
            <List sx={{ pt: 0 }}>
              {includesList.map((item, index) => <BulletItem key={index} text={item} />)}
            </List>
          </Box>
        )}

        <Divider sx={{ mb: 4 }} />

        {/* Quién lo imparte Section */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
            Quién lo imparte
          </Typography>
          {personas.length === 0 && (
            <Typography variant="body2" color="text.secondary">{profile?.nombre || 'Nombre no disponible'}</Typography>
          )}
          {personas.map((persona) => (
            <Box key={persona._id} sx={{ mb: 2 }}>
              <Link
                component={RouterLink}
                to={`/perfil/${persona._id}`}
                underline="none"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 1.5,
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.8 }
                }}
              >
                <Avatar src={fotoDe(persona, 'w_48,h_48,c_fill') || undefined} sx={{ width: 48, height: 48 }} />
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary.main">
                    {nombreDe(persona) || 'Nombre no disponible'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {persona.rol || 'Facilitador'}
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6, whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                {persona.desc || `Hola, soy ${persona.name || 'tu facilitador'}. Estoy aquí para ayudarte a alcanzar tus objetivos con este servicio.`}
              </Typography>
            </Box>
          ))}
          {/* En un grupo o negocio: de qué página es y quién atiende las reservas y los mensajes */}
          {grupo && (
            <Box sx={{ mt: 1, p: 1.5, borderRadius: '12px', bgcolor: 'sutil' }}>
              <Typography variant="body2" color="primary.main">
                Servicio de{' '}
                <Link component={RouterLink} to={`/pagina/${grupo._id}`} underline="hover" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                  {grupo.nombre || 'esta página'}
                </Link>
              </Typography>
              {nombreProveedor && (
                <Typography variant="caption" color="text.secondary">
                  Las reservas y los mensajes los atiende {nombreProveedor}.
                </Typography>
              )}
            </Box>
          )}
        </Box>


        {/* Info adicional Section */}
        {additionalInfoList.length > 0 && (
          <>
            <Divider sx={{ mb: 4 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight="bold" color="primary.main" gutterBottom>
                Info adicional
              </Typography>
              <List sx={{ pt: 0 }}>
                {additionalInfoList.map((item, index) => <BulletItem key={index} text={item} color="success.claro" />)}
              </List>
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ServiceContent;
